import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { useSortable } from '@dnd-kit/react/sortable';
import {
  Plus, Pencil, Trash2, Loader2, Save, X, GripVertical,
  ChevronDown, ChevronUp, MapPin, Clock, Euro, Image as ImageIcon,
  Languages, ArrowLeft, Upload, Check, AlertTriangle, RotateCcw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============= SORTABLE TOUR ITEM =============
const SortableTourItem = ({ tour, index, onEdit, onDelete, onExpand, expanded }) => {
  const { ref } = useSortable({ id: tour.id, index, type: 'tour', accept: 'tour', group: 'tours' });
  return (
    <div ref={ref} className="bg-white rounded-xl border border-[#E7E5E4] overflow-hidden" data-testid={`tour-card-${tour.id}`}>
      <div className="flex items-center gap-3 p-4 hover:bg-[#FDFBF7] transition-colors">
        <div className="cursor-grab active:cursor-grabbing touch-none p-1" data-testid={`tour-drag-${tour.id}`}>
          <GripVertical className="h-5 w-5 text-[#A8A29E]" />
        </div>
        {tour.image && (
          <img src={tour.image} alt={tour.name?.es} className="h-14 w-14 rounded-lg object-cover flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onExpand(tour.id)}>
          <h3 className="font-semibold text-[#1C1917] truncate">{tour.name?.es || tour.id}</h3>
          <div className="flex items-center gap-4 mt-1 text-sm text-[#78716C]">
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {tour.duration}</span>
            <span className="flex items-center gap-1"><Euro className="h-3.5 w-3.5" /> {tour.price}</span>
            <span className="text-xs bg-[#F5F3EF] px-2 py-0.5 rounded-full">#{tour.sort_order ?? index}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(tour)} data-testid={`tour-edit-${tour.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => onDelete(tour.id, tour.name?.es)} className="text-red-600 hover:text-red-700 hover:border-red-300" data-testid={`tour-delete-${tour.id}`}><Trash2 className="h-4 w-4" /></Button>
          {expanded ? <ChevronUp className="h-5 w-5 text-[#A8A29E]" /> : <ChevronDown className="h-5 w-5 text-[#A8A29E]" />}
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#E7E5E4] pt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            {['es', 'en', 'fr'].map(lang => (
              <div key={lang}><span className="font-medium text-[#78716C] uppercase text-xs">{lang}:</span>
                <p className="text-[#1C1917] mt-1 line-clamp-2">{tour.description?.[lang] || '-'}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============= MEDIA PICKER MODAL =============
const MediaPickerModal = ({ open, onClose, onSelect, token }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/admin/media`, { headers })
      .then(res => setFiles(res.data))
      .catch(() => toast.error('Error cargando media'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleUpload = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
    setUploading(true);
    for (const file of selectedFiles) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await axios.post(`${API}/admin/media/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        setFiles(prev => [{ id: res.data.id, original_filename: file.name, size: res.data.size, ...res.data }, ...prev]);
      } catch { toast.error(`Error subiendo ${file.name}`); }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Seleccionar imagen</DialogTitle>
        </DialogHeader>
        <div className="flex gap-3 mb-4">
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" size="sm" data-testid="media-picker-upload">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            {uploading ? 'Subiendo...' : 'Subir nueva'}
          </Button>
        </div>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#C2410C]" /></div>
        ) : (
          <div className="grid grid-cols-4 gap-3">
            {files.filter(f => !f.is_deleted).map(file => (
              <button
                key={file.id}
                onClick={() => { onSelect(file); onClose(); }}
                className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-[#C2410C] transition-colors"
                data-testid={`media-pick-${file.id}`}
              >
                <img src={`${API}/media/file/${file.id}`} alt={file.original_filename} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============= GALLERY EDITOR =============
const GalleryEditor = ({ gallery = [], onChange, token }) => {
  const [showPicker, setShowPicker] = useState(false);

  const items = (gallery || []).map((item, i) => {
    if (typeof item === 'string') return { url: item, media_id: null, sort_order: i };
    return { ...item, sort_order: item.sort_order ?? i };
  });

  const addImage = (file) => {
    const newItem = { media_id: file.id, url: `${API}/media/file/${file.id}`, sort_order: items.length };
    onChange([...items, newItem]);
  };

  const removeImage = (idx) => {
    const updated = items.filter((_, i) => i !== idx).map((item, i) => ({ ...item, sort_order: i }));
    onChange(updated);
  };

  const moveImage = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    onChange(arr.map((item, i) => ({ ...item, sort_order: i })));
  };

  return (
    <div data-testid="gallery-editor">
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium text-[#1C1917]">Galeria ({items.length} imagenes)</label>
        <Button type="button" variant="outline" size="sm" onClick={() => setShowPicker(true)} data-testid="gallery-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Añadir
        </Button>
      </div>
      {items.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {items.map((item, idx) => (
            <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-[#E7E5E4]">
              <img src={item.url || `${API}/media/file/${item.media_id}`} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                {idx > 0 && <button onClick={() => moveImage(idx, -1)} className="p-1.5 bg-white rounded-full text-xs"><RotateCcw className="h-3 w-3" /></button>}
                <button onClick={() => removeImage(idx)} className="p-1.5 bg-white rounded-full"><Trash2 className="h-3 w-3 text-red-600" /></button>
                {idx < items.length - 1 && <button onClick={() => moveImage(idx, 1)} className="p-1.5 bg-white rounded-full text-xs rotate-180"><RotateCcw className="h-3 w-3" /></button>}
              </div>
              <span className="absolute top-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">{idx + 1}</span>
            </div>
          ))}
        </div>
      )}
      <MediaPickerModal open={showPicker} onClose={() => setShowPicker(false)} onSelect={addImage} token={token} />
    </div>
  );
};

// ============= POI EDITOR =============
const POIEditor = ({ tourId, token }) => {
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPoi, setEditingPoi] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPois = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/tours/${tourId}/pois`, { headers });
      setPois(res.data);
    } catch { toast.error('Error cargando POIs'); }
    finally { setLoading(false); }
  }, [tourId]);

  useEffect(() => { if (tourId) fetchPois(); }, [tourId, fetchPois]);

  const savePoi = async (data) => {
    setSaving(true);
    try {
      if (data.id && pois.find(p => p.id === data.id)) {
        await axios.put(`${API}/admin/tours/${tourId}/pois/${data.id}`, data, { headers });
        toast.success('POI actualizado');
      } else {
        const res = await axios.post(`${API}/admin/tours/${tourId}/pois`, data, { headers });
        data.id = res.data.id;
        toast.success('POI creado');
      }
      setEditingPoi(null);
      fetchPois();
    } catch { toast.error('Error guardando POI'); }
    finally { setSaving(false); }
  };

  const deletePoi = async (poiId) => {
    if (!window.confirm('¿Eliminar este punto de interes?')) return;
    try {
      await axios.delete(`${API}/admin/tours/${tourId}/pois/${poiId}`, { headers });
      toast.success('POI eliminado');
      fetchPois();
    } catch { toast.error('Error eliminando POI'); }
  };

  const translatePoi = async (poiId) => {
    setTranslating(true);
    try {
      await axios.post(`${API}/admin/tours/${tourId}/pois/${poiId}/translate`, { fields: ['name', 'short_description', 'long_description'], languages: ['en', 'fr'] }, { headers });
      toast.success('POI traducido');
      fetchPois();
    } catch { toast.error('Error traduciendo POI'); }
    finally { setTranslating(false); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#C2410C]" /></div>;

  return (
    <div data-testid="poi-editor">
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium text-[#1C1917]">Puntos de interes ({pois.length})</label>
        <Button type="button" variant="outline" size="sm" onClick={() => setEditingPoi({ name: { es: '', en: '', fr: '' }, short_description: { es: '', en: '', fr: '' }, long_description: { es: '', en: '', fr: '' }, address: '', coordinates: { lat: 0, lng: 0 } })} data-testid="poi-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Añadir
        </Button>
      </div>
      <div className="space-y-2">
        {pois.map((poi, idx) => (
          <div key={poi.id} className="flex items-center gap-3 p-3 bg-[#F5F3EF] rounded-lg" data-testid={`poi-item-${poi.id}`}>
            <span className="w-6 h-6 rounded-full bg-[#C2410C] text-white flex items-center justify-center text-xs flex-shrink-0">{idx + 1}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-[#1C1917] truncate">{poi.name?.es || 'Sin nombre'}</p>
              <p className="text-xs text-[#78716C] truncate">{poi.address || poi.short_description?.es || ''}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => translatePoi(poi.id)} disabled={translating} title="Traducir">
              <Languages className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditingPoi(poi)}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => deletePoi(poi.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>

      {/* POI Edit Dialog */}
      <Dialog open={!!editingPoi} onOpenChange={() => setEditingPoi(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingPoi?.id ? 'Editar' : 'Nuevo'} Punto de Interes</DialogTitle></DialogHeader>
          {editingPoi && <POIForm poi={editingPoi} onSave={savePoi} onCancel={() => setEditingPoi(null)} saving={saving} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const POIForm = ({ poi, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(poi);
  const [tab, setTab] = useState('es');

  const update = (field, val) => setForm(prev => ({ ...prev, [field]: val }));
  const updateLang = (field, lang, val) => setForm(prev => ({ ...prev, [field]: { ...prev[field], [lang]: val } }));

  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-[#E7E5E4] pb-2">
        {['es', 'en', 'fr'].map(l => (
          <button key={l} type="button" onClick={() => setTab(l)} className={`px-3 py-1.5 rounded-t text-sm font-medium ${tab === l ? 'bg-[#C2410C] text-white' : 'text-[#78716C] hover:bg-[#F5F3EF]'}`}>{l.toUpperCase()}</button>
        ))}
      </div>
      <div><label className="text-sm font-medium">Nombre ({tab.toUpperCase()})</label>
        <Input value={form.name?.[tab] || ''} onChange={e => updateLang('name', tab, e.target.value)} data-testid="poi-name-input" /></div>
      <div><label className="text-sm font-medium">Descripcion corta ({tab.toUpperCase()})</label>
        <Input value={form.short_description?.[tab] || ''} onChange={e => updateLang('short_description', tab, e.target.value)} data-testid="poi-short-desc-input" /></div>
      <div><label className="text-sm font-medium">Descripcion larga ({tab.toUpperCase()})</label>
        <Textarea value={form.long_description?.[tab] || ''} onChange={e => updateLang('long_description', tab, e.target.value)} rows={3} data-testid="poi-long-desc-input" /></div>
      <div><label className="text-sm font-medium">Direccion</label>
        <Input value={form.address || ''} onChange={e => update('address', e.target.value)} placeholder="Calle Bailen, Madrid" data-testid="poi-address-input" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-sm font-medium">Latitud</label>
          <Input type="number" step="0.0001" value={form.coordinates?.lat || ''} onChange={e => update('coordinates', { ...form.coordinates, lat: parseFloat(e.target.value) || 0 })} data-testid="poi-lat-input" /></div>
        <div><label className="text-sm font-medium">Longitud</label>
          <Input type="number" step="0.0001" value={form.coordinates?.lng || ''} onChange={e => update('coordinates', { ...form.coordinates, lng: parseFloat(e.target.value) || 0 })} data-testid="poi-lng-input" /></div>
      </div>
      <div className="flex gap-3 pt-3 border-t">
        <Button onClick={() => onSave(form)} disabled={saving} className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="poi-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Guardar
        </Button>
        <Button variant="outline" onClick={onCancel}><X className="h-4 w-4 mr-1" /> Cancelar</Button>
      </div>
    </div>
  );
};

// ============= MAP ROUTE EDITOR =============
const MapRouteEditor = ({ mapRoute = [], onChange }) => {
  const [editIdx, setEditIdx] = useState(null);
  const points = (mapRoute || []).map((p, i) => ({ ...p, sort_order: p.sort_order ?? i }));

  const addPoint = () => {
    onChange([...points, { lat: 40.417, lng: -3.703, name: `Punto ${points.length + 1}`, address: '', sort_order: points.length }]);
  };

  const updatePoint = (idx, field, val) => {
    const updated = [...points];
    updated[idx] = { ...updated[idx], [field]: val };
    onChange(updated);
  };

  const removePoint = (idx) => {
    onChange(points.filter((_, i) => i !== idx).map((p, i) => ({ ...p, sort_order: i })));
  };

  const movePoint = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= points.length) return;
    const arr = [...points];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    onChange(arr.map((p, i) => ({ ...p, sort_order: i })));
  };

  return (
    <div data-testid="map-route-editor">
      <div className="flex items-center justify-between mb-3">
        <label className="text-sm font-medium text-[#1C1917]">Ruta del mapa ({points.length} paradas)</label>
        <Button type="button" variant="outline" size="sm" onClick={addPoint} data-testid="map-add-point-btn"><Plus className="h-4 w-4 mr-1" /> Añadir parada</Button>
      </div>
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {points.map((point, idx) => (
          <div key={idx} className="flex items-start gap-2 p-2 bg-[#F5F3EF] rounded-lg" data-testid={`map-point-${idx}`}>
            <div className="flex flex-col gap-0.5 pt-1">
              <button onClick={() => movePoint(idx, -1)} disabled={idx === 0} className="text-[#A8A29E] hover:text-[#1C1917] disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
              <span className="w-5 h-5 rounded-full bg-[#C2410C] text-white flex items-center justify-center text-[10px]">{idx + 1}</span>
              <button onClick={() => movePoint(idx, 1)} disabled={idx === points.length - 1} className="text-[#A8A29E] hover:text-[#1C1917] disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
            </div>
            <div className="flex-1 grid grid-cols-4 gap-2">
              <Input value={point.name || ''} onChange={e => updatePoint(idx, 'name', e.target.value)} placeholder="Nombre" className="text-sm col-span-2" />
              <Input type="number" step="0.0001" value={point.lat || ''} onChange={e => updatePoint(idx, 'lat', parseFloat(e.target.value) || 0)} placeholder="Lat" className="text-sm" />
              <Input type="number" step="0.0001" value={point.lng || ''} onChange={e => updatePoint(idx, 'lng', parseFloat(e.target.value) || 0)} placeholder="Lng" className="text-sm" />
            </div>
            <button onClick={() => removePoint(idx)} className="p-1 text-red-500 hover:text-red-700 mt-1"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      {points.length === 0 && <p className="text-sm text-[#A8A29E] text-center py-4">Sin paradas definidas. Añade paradas para crear la ruta.</p>}
    </div>
  );
};

// ============= MAIN TOUR FORM =============
const TourForm = ({ tour, onSave, onCancel, saving, token }) => {
  const [form, setForm] = useState(tour);
  const [tab, setTab] = useState('es');
  const [section, setSection] = useState('general');
  const [translating, setTranslating] = useState(false);
  const [showMainImagePicker, setShowMainImagePicker] = useState(false);
  const isNew = !tour.id || tour.id === '';

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateLang = (field, lang, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: { ...prev[field], [lang]: value } };
      // Mark translation as manually edited
      if (lang !== 'es' && prev.translation_meta?.[field]?.[lang]?.auto) {
        updated.translation_meta = {
          ...prev.translation_meta,
          [field]: { ...prev.translation_meta?.[field], [lang]: { auto: true, edited: true } }
        };
      }
      return updated;
    });
  };

  const handleTranslate = async () => {
    if (!form.id || isNew) { toast.error('Guarda primero el tour'); return; }
    setTranslating(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/tours/${form.id}/translate`, {
        fields: ['name', 'description', 'full_description'],
        languages: ['en', 'fr'],
        force: false
      }, { headers });
      setForm(prev => ({ ...prev, ...res.data }));
      toast.success('Traducciones generadas');
    } catch { toast.error('Error traduciendo'); }
    finally { setTranslating(false); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name?.es?.trim()) { toast.error('Nombre (ES) obligatorio'); return; }
    if (!form.price) { toast.error('Precio obligatorio'); return; }
    onSave(form);
  };

  const sections = [
    { key: 'general', label: 'General', icon: Pencil },
    { key: 'gallery', label: 'Galeria', icon: ImageIcon },
    { key: 'pois', label: 'Puntos de interes', icon: MapPin },
    { key: 'map', label: 'Mapa / Ruta', icon: MapPin },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="tour-form">
      {/* Section Tabs */}
      <div className="flex gap-1 bg-[#F5F3EF] p-1 rounded-lg overflow-x-auto">
        {sections.map(s => (
          <button key={s.key} type="button" onClick={() => setSection(s.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${section === s.key ? 'bg-white text-[#C2410C] shadow-sm' : 'text-[#78716C] hover:text-[#1C1917]'}`}>
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>

      {/* GENERAL SECTION */}
      {section === 'general' && (
        <div className="space-y-5">
          {/* Language tabs + translate */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {['es', 'en', 'fr'].map(l => (
                <button key={l} type="button" onClick={() => setTab(l)} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === l ? 'bg-[#C2410C] text-white' : 'text-[#78716C] hover:bg-[#F5F3EF]'}`}>{l === 'es' ? 'Espanol' : l === 'en' ? 'English' : 'Francais'}</button>
              ))}
            </div>
            {!isNew && (
              <Button type="button" variant="outline" size="sm" onClick={handleTranslate} disabled={translating} data-testid="tour-translate-btn">
                {translating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Languages className="h-4 w-4 mr-1" />}
                Auto-traducir EN/FR
              </Button>
            )}
          </div>

          {/* Translation warning */}
          {tab !== 'es' && form.translation_meta?.description?.[tab]?.auto && !form.translation_meta?.description?.[tab]?.edited && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
              <AlertTriangle className="h-3.5 w-3.5" /> Traduccion autogenerada. Edita para personalizar.
            </div>
          )}
          {tab !== 'es' && form.translation_meta?.description?.[tab]?.edited && (
            <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg">
              <Check className="h-3.5 w-3.5" /> Editado manualmente.
            </div>
          )}

          {/* Name */}
          <div><label className="block text-sm font-medium text-[#1C1917] mb-1">Nombre ({tab.toUpperCase()}) {tab === 'es' && '*'}</label>
            <Input value={form.name?.[tab] || ''} onChange={e => updateLang('name', tab, e.target.value)} data-testid="tour-name-input" /></div>

          {/* Description */}
          <div><label className="block text-sm font-medium text-[#1C1917] mb-1">Descripcion corta ({tab.toUpperCase()})</label>
            <Textarea value={form.description?.[tab] || ''} onChange={e => updateLang('description', tab, e.target.value)} rows={2} data-testid="tour-desc-input" /></div>

          {/* Full Description */}
          <div><label className="block text-sm font-medium text-[#1C1917] mb-1">Descripcion completa ({tab.toUpperCase()})</label>
            <Textarea value={form.full_description?.[tab] || ''} onChange={e => updateLang('full_description', tab, e.target.value)} rows={4} data-testid="tour-full-desc-input" /></div>

          {/* Price & Duration */}
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium mb-1">Precio *</label>
              <div className="relative"><Euro className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A8A29E]" />
                <Input type="number" min="0" step="0.01" value={form.price || ''} onChange={e => updateField('price', parseFloat(e.target.value) || 0)} className="pl-9" data-testid="tour-price-input" /></div></div>
            <div><label className="block text-sm font-medium mb-1">Duracion (texto)</label>
              <Input value={form.duration || ''} onChange={e => updateField('duration', e.target.value)} placeholder="90 minutos" data-testid="tour-duration-input" /></div>
            <div><label className="block text-sm font-medium mb-1">Duracion (horas)</label>
              <Input type="number" min="0.5" step="0.5" value={form.duration_hours || ''} onChange={e => updateField('duration_hours', parseFloat(e.target.value) || 1)} data-testid="tour-duration-hours-input" /></div>
          </div>

          {/* Main Image */}
          <div>
            <label className="block text-sm font-medium text-[#1C1917] mb-1">Imagen principal</label>
            <div className="flex gap-3 items-end">
              <Input value={form.image || ''} onChange={e => updateField('image', e.target.value)} placeholder="URL de imagen" className="flex-1" data-testid="tour-image-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => setShowMainImagePicker(true)} data-testid="tour-image-picker-btn"><ImageIcon className="h-4 w-4 mr-1" /> Media</Button>
            </div>
            {form.image && <img src={form.image} alt="Preview" className="mt-2 h-24 rounded-lg object-cover" />}
          </div>
          <MediaPickerModal open={showMainImagePicker} onClose={() => setShowMainImagePicker(false)} onSelect={file => updateField('image', `${API}/media/file/${file.id}`)} token={token} />
        </div>
      )}

      {/* GALLERY SECTION */}
      {section === 'gallery' && (
        <GalleryEditor gallery={form.gallery} onChange={val => updateField('gallery', val)} token={token} />
      )}

      {/* POI SECTION */}
      {section === 'pois' && (
        isNew ? <p className="text-sm text-[#78716C] py-8 text-center">Guarda el tour primero para gestionar puntos de interes.</p>
          : <POIEditor tourId={form.id} token={token} />
      )}

      {/* MAP SECTION */}
      {section === 'map' && (
        <MapRouteEditor mapRoute={form.map_route} onChange={val => updateField('map_route', val)} />
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-[#E7E5E4] sticky bottom-0 bg-white py-4">
        <Button type="submit" disabled={saving} className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="tour-save-btn">
          {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Guardando...</> : <><Save className="h-4 w-4 mr-2" /> Guardar</>}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="tour-cancel-btn"><X className="h-4 w-4 mr-2" /> Cancelar</Button>
      </div>
    </form>
  );
};

// ============= EMPTY TOUR TEMPLATE =============
const EMPTY_TOUR = {
  id: '',
  name: { es: '', en: '', fr: '' },
  description: { es: '', en: '', fr: '' },
  full_description: { es: '', en: '', fr: '' },
  duration: '',
  duration_hours: 1,
  price: 0,
  includes: [],
  highlights: [],
  image: '',
  gallery: [],
  map_route: [],
  route_details: [],
  sort_order: 0,
  translation_meta: {},
};

// ============= MAIN ADMIN TOURS PAGE =============
const AdminTours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTour, setEditingTour] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [orderChanged, setOrderChanged] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const snapshotRef = useRef(null);

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTours = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/tours`, { headers });
      setTours(res.data);
      setOrderChanged(false);
    } catch { toast.error('Error cargando tours'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTours(); }, [fetchTours]);

  const handleSave = async (tourData) => {
    setSaving(true);
    try {
      if (editingTour === 'new') {
        await axios.post(`${API}/admin/tours`, tourData, { headers });
        toast.success('Tour creado');
      } else {
        await axios.put(`${API}/admin/tours/${tourData.id}`, tourData, { headers });
        toast.success('Tour actualizado');
      }
      setEditingTour(null);
      fetchTours();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async (tourId, tourName) => {
    if (!window.confirm(`¿Eliminar "${tourName}"?`)) return;
    try {
      await axios.delete(`${API}/admin/tours/${tourId}`, { headers });
      toast.success('Tour eliminado');
      fetchTours();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const order = tours.map((t, i) => ({ id: t.id, sort_order: i }));
      await axios.put(`${API}/admin/tours/reorder`, { order }, { headers });
      toast.success('Orden guardado');
      setOrderChanged(false);
    } catch { toast.error('Error guardando orden'); }
    finally { setSavingOrder(false); }
  };

  // Drag and drop handlers
  const handleDragStart = () => {
    snapshotRef.current = [...tours];
  };

  const handleDragEnd = (event) => {
    if (event.canceled) {
      setTours(snapshotRef.current);
      return;
    }
    const { source } = event.operation;
    if (source && typeof source.initialIndex === 'number' && typeof source.index === 'number') {
      const fromIdx = source.initialIndex;
      const toIdx = source.index;
      if (fromIdx !== toIdx) {
        setTours(prev => {
          const arr = [...prev];
          const [moved] = arr.splice(fromIdx, 1);
          arr.splice(toIdx, 0, moved);
          return arr;
        });
        setOrderChanged(true);
      }
    }
  };

  // Edit mode
  if (editingTour !== null) {
    return (
      <div className="p-6 lg:p-8" data-testid="admin-tours-edit">
        <button onClick={() => setEditingTour(null)} className="flex items-center gap-2 text-[#57534E] hover:text-[#1C1917] mb-4 text-sm font-medium">
          <ArrowLeft className="h-4 w-4" /> Volver al listado
        </button>
        <h1 className="text-2xl font-bold text-[#1C1917] mb-6">
          {editingTour === 'new' ? 'Nuevo Tour' : `Editar: ${editingTour?.name?.es || ''}`}
        </h1>
        <div className="bg-white rounded-xl border border-[#E7E5E4] p-6">
          <TourForm tour={editingTour === 'new' ? EMPTY_TOUR : editingTour} onSave={handleSave} onCancel={() => setEditingTour(null)} saving={saving} token={token} />
        </div>
      </div>
    );
  }

  // List mode
  return (
    <div className="p-6 lg:p-8" data-testid="admin-tours-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1C1917]">Gestion de Tours</h1>
          <p className="text-[#78716C] text-sm mt-1">{tours.length} tour(s) — Arrastra para reordenar</p>
        </div>
        <div className="flex gap-3">
          {orderChanged && (
            <Button onClick={handleSaveOrder} disabled={savingOrder} className="bg-green-600 hover:bg-green-700" data-testid="tour-save-order-btn">
              {savingOrder ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Guardar orden
            </Button>
          )}
          <Button onClick={() => setEditingTour('new')} className="bg-[#C2410C] hover:bg-[#9A3412]" data-testid="tour-create-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuevo Tour
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C2410C]" /></div>
      ) : tours.length === 0 ? (
        <div className="text-center py-20 text-[#78716C]">
          <MapPin className="h-16 w-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">No hay tours</p>
        </div>
      ) : (
        <DragDropProvider onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="space-y-3">
            {tours.map((tour, index) => (
              <SortableTourItem
                key={tour.id}
                tour={tour}
                index={index}
                onEdit={setEditingTour}
                onDelete={handleDelete}
                onExpand={(id) => setExpandedId(expandedId === id ? null : id)}
                expanded={expandedId === tour.id}
              />
            ))}
          </div>
        </DragDropProvider>
      )}
    </div>
  );
};

export default AdminTours;
